import type {HTMLAttributes} from 'react';

import {cn} from '../../helpers/cn';

export type LoadingSize = 'small' | 'medium' | 'large';
export type LoadingColor = 'primary' | 'neutral' | 'inverse' | 'inherit';

export type LoadingProps = HTMLAttributes<HTMLDivElement> & {
  color?: LoadingColor;
  mId?: string;
  size?: LoadingSize | string;
  inline?: boolean;
};

const dotSizeMap: Record<LoadingSize, number> = {
  small: 6,
  medium: 12,
  large: 16
};

const dotGapMap: Record<LoadingSize, string> = {
  small: 'gap-1',
  medium: 'gap-1.5',
  large: 'gap-2'
};

const dotColorMap: Record<LoadingColor, string> = {
  primary: 'bg-orange-main',
  neutral: 'bg-gray-08',
  inverse: 'bg-white',
  inherit: 'bg-current'
};

function resolveSize(size: LoadingSize | string): LoadingSize {
  if (size in dotSizeMap) {
    return size as LoadingSize;
  }

  return 'small';
}

export function Loading({
  color = 'primary',
  mId,
  size = 'small',
  inline = false,
  className,
  ...props
}: LoadingProps) {
  const resolvedSize = resolveSize(size);
  const dotSize = dotSizeMap[resolvedSize];

  return (
    <div
      {...props}
      className={cn(
        'flex items-center justify-center',
        inline ? 'h-auto w-auto' : 'h-full w-full text-center',
        dotGapMap[resolvedSize],
        className
      )}
    >
      {[0, 0.5, 1].map((delay, index) => (
        <span
          key={index}
          id={mId}
          aria-hidden
          className={cn(
            'inline-block shrink-0 animate-[button-dot_1.5s_ease-in-out_infinite] rounded-[10px]',
            dotColorMap[color]
          )}
          style={{
            width: dotSize,
            height: dotSize,
            animationDelay: `${delay}s`
          }}
        />
      ))}
    </div>
  );
}
